import { useEffect, useMemo, useState } from "react";
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';

/**
 * Top Gaffer stage configuration for Season competitions.
 *
 * Controlled component. Uses the platform's existing Tailwind styling
 * (greens #284239, orange #BA6830) so it matches the surrounding admin form.
 *
 * Props:
 *   availableMatchdays: array of { match_day:number, first_kickoff_time?:string }
 *                       — the competition's matchdays (start_match_day → end of season).
 *   value:    { enabled:boolean, stageCount:number, stages:number[][] }
 *   onChange: (nextValue) => void
 *   disabled: boolean — read-only (e.g. once a competition has started)
 */
export default function TopGafferConfig({
  availableMatchdays = [],
  value,
  onChange,
  disabled = false,
}) {
  const enabled = value?.enabled || false;
  const stageCount = value?.stageCount || 1;
  const stages = value?.stages || [];

  // Local text for the stage-count field so it can be cleared while typing
  // (reverts to a valid value on blur). Kept in sync with the committed count.
  const [stageCountText, setStageCountText] = useState(String(stageCount));
  useEffect(() => {
    setStageCountText(String(stageCount));
  }, [stageCount]);

  // Sorted list of available matchday numbers, and a label lookup for kickoff dates.
  const mdNumbers = useMemo(
    () => availableMatchdays.map((m) => m.match_day).sort((a, b) => a - b),
    [availableMatchdays]
  );
  const total = mdNumbers.length;

  // matchday number -> index of the stage that currently holds it
  const ownerOf = useMemo(() => {
    const map = {};
    stages.forEach((mds, i) => (mds || []).forEach((md) => { map[md] = i; }));
    return map;
  }, [stages]);

  const allocatedCount = Object.keys(ownerOf).length;
  const unallocated = mdNumbers.filter((md) => ownerOf[md] === undefined);

  function resize(arr, n) {
    const out = [];
    for (let i = 0; i < n; i++) out.push(arr[i] ? [...arr[i]] : []);
    return out;
  }

  function emit(next) {
    if (!disabled) onChange?.(next);
  }

  // Prune allocations that are no longer available (e.g. after changing league/season/start).
  useEffect(() => {
    if (!enabled) return;
    const set = new Set(mdNumbers);
    const pruned = stages.map((mds) => (mds || []).filter((md) => set.has(md)));
    if (JSON.stringify(pruned) !== JSON.stringify(stages)) {
      emit({ enabled, stageCount, stages: pruned });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdNumbers]);

  function setEnabled(on) {
    emit({ enabled: on, stageCount: stageCount || 1, stages: resize(stages, stageCount || 1) });
  }

  function onStageCountChange(e) {
    const raw = e.target.value;
    setStageCountText(raw);
    if (raw === '') return; // allow the field to be empty while typing
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) {
      const clamped = Math.min(total || 1, n);
      emit({ enabled, stageCount: clamped, stages: resize(stages, clamped) });
    }
  }

  function onStageCountBlur() {
    const n = parseInt(stageCountText, 10);
    const clamped = Number.isFinite(n) && n >= 1 ? Math.min(total || 1, n) : 1;
    setStageCountText(String(clamped));
    if (clamped !== stageCount) {
      emit({ enabled, stageCount: clamped, stages: resize(stages, clamped) });
    }
  }

  function toggleMatchday(stageIdx, md) {
    const next = resize(stages, stageCount);
    for (let i = 0; i < next.length; i++) next[i] = next[i].filter((x) => x !== md);
    if (ownerOf[md] !== stageIdx) {
      next[stageIdx] = [...next[stageIdx], md].sort((a, b) => a - b);
    }
    emit({ enabled, stageCount, stages: next });
  }

  function autoDivide() {
    const n = stageCount;
    const base = Math.floor(total / n);
    const rem = total % n;
    const out = [];
    let idx = 0;
    for (let i = 0; i < n; i++) {
      const size = base + (i < rem ? 1 : 0);
      out.push(mdNumbers.slice(idx, idx + size));
      idx += size;
    }
    emit({ enabled, stageCount: n, stages: out });
  }

  // A stage has a "gap" when its matchdays aren't contiguous within the available list.
  function hasGap(mds) {
    if (!mds || mds.length < 2) return false;
    const idxs = mds.map((md) => mdNumbers.indexOf(md)).sort((a, b) => a - b);
    return idxs[idxs.length - 1] - idxs[0] + 1 !== idxs.length;
  }

  return (
    <FcCard as="div" padding="standard" className="mb-2 p-3 border rounded bg-surface-card">
      <label className="flex items-center gap-2 text-ui-green font-medium">
        <FcInput aria-label="Enable Top Gaffer"
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable Top Gaffer
      </label>
      <p className="text-xs text-content-secondary mt-1 ml-6">
        Create separate stage leaderboards throughout the season.
      </p>

      {enabled && total === 0 && (
        <p className="text-sm text-orange-600 mt-3">
          Select a league, season and start matchday first — the available matchdays will load here.
        </p>
      )}

      {enabled && total > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap items-end gap-3 mb-2">
            <div>
              <label className="block text-sm font-medium mb-1 text-ui-green">
                How many Top Gaffer stages?
              </label>
              <FcInput aria-label="How many Top Gaffer stages?"
                type="number"
                min={1}
                max={total}
                inputMode="numeric"
                value={stageCountText}
                disabled={disabled}
                onChange={onStageCountChange}
                onBlur={onStageCountBlur}
                className="block w-24 border"
              />
            </div>
            <FcButton variant="tertiary"
              type="button"
              onClick={autoDivide}
              disabled={disabled}
              className=""
            >
              Automatically divide matchdays
            </FcButton>
          </div>
          <p className="text-xs text-content-secondary mb-3">
            Maximum {total} stages (one per available matchday). Each matchday can belong to only one stage.
          </p>

          {Array.from({ length: stageCount }).map((_, stageIdx) => {
            const mine = stages[stageIdx] || [];
            const empty = mine.length === 0;
            const gap = hasGap(mine);
            return (
              <div key={stageIdx} className="mb-3 p-3 border rounded bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-action-primary">
                    Stage {stageIdx + 1}
                  </span>
                  <span className="text-xs text-content-secondary">
                    {mine.length} matchday{mine.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {mdNumbers.map((md) => {
                    const owner = ownerOf[md];
                    const checkedHere = owner === stageIdx;
                    const ownedElsewhere = owner !== undefined && owner !== stageIdx;
                    return (
                      <label
                        key={md}
                        title={ownedElsewhere ? `MD${md} — allocated to Stage ${owner + 1}` : `MD${md}`}
                        className={
                          "inline-flex items-center gap-1 text-sm border rounded px-2 py-1 " +
                          (checkedHere
                            ? "bg-ui-green text-white border-[#284239] "
                            : "text-ui-green bg-surface-card ") +
                          (ownedElsewhere || disabled
                            ? "opacity-40 cursor-not-allowed"
                            : "cursor-pointer hover:border-ui-border")
                        }
                      >
                        <FcInput aria-label="MD"
                          type="checkbox"
                          className="sr-only"
                          checked={checkedHere}
                          disabled={disabled || ownedElsewhere}
                          onChange={() => toggleMatchday(stageIdx, md)}
                        />
                        MD{md}
                      </label>
                    );
                  })}
                </div>
                {empty && (
                  <p className="text-xs text-orange-600 mt-2">
                    Stage {stageIdx + 1} must contain at least one matchday.
                  </p>
                )}
                {gap && (
                  <p className="text-xs text-orange-600 mt-2">
                    This stage contains a gap between matchdays.
                  </p>
                )}
              </div>
            );
          })}

          <div className="text-sm text-ui-green mt-2">
            {allocatedCount} of {total} matchdays allocated across {stageCount} stage
            {stageCount === 1 ? "" : "s"}.
            {unallocated.length > 0 && (
              <span className="text-orange-600">
                {" "}
                {unallocated.map((md) => `MD${md}`).join(", ")} remain unallocated.
              </span>
            )}
          </div>
        </div>
      )}
    </FcCard>
  );
}
